import type { ThreeEvent } from '@react-three/fiber';
import { createContext, useContext, useCallback, useEffect } from 'react';
import * as THREE from 'three';
import { useAppStore } from '../state/store';
import { useDocumentStore } from '../state/useDocumentStore';

/**
 * Entity click handler registry.
 *
 * SceneContent owns the selection-tool click router; isolated layers
 * (EntityLayer, instanced groups, BIM overlays) publish clicks here, and
 * NURBS selection bridges subscribe as extra listeners so the active tool
 * can react without SceneContent instantiating every tool hook.
 */
export type EntityClickHandler = (entityId: string, event: ThreeEvent<MouseEvent>) => void;

const mainRouter: { current: EntityClickHandler | null } = { current: null };
const listeners = new Set<EntityClickHandler>();

/** Register the primary router (SceneContent selection routing). */
export function setEntityClickRouter(handler: EntityClickHandler | null): void {
    mainRouter.current = handler;
}

/** Subscribe an additional listener (e.g. NURBS selection bridges). Returns unsubscribe. */
export function addEntityClickListener(handler: EntityClickHandler): () => void {
    listeners.add(handler);
    return () => { listeners.delete(handler); };
}

/**
 * Dispatch an entity click to the router and all listeners.
 *
 * A4: the gate reads the DOCUMENT SCENE LEDGER, not the CAD store — so the
 * same lock/visibility enforcement applies to every workbench's objects
 * (BIM elements and CAD entities alike). Unknown ids still dispatch (legacy
 * ids from kernels without ledger entries); protocol-level visibility and
 * locked layers are swallowed at this single dispatch point.
 */
export function dispatchEntityClick(entityId: string, event: ThreeEvent<MouseEvent>): void {
    const sceneObject = useDocumentStore.getState().sceneObjects[entityId];
    if (sceneObject) {
        if (!sceneObject.visible || sceneObject.locked) return;
        const layer = useAppStore.getState().layers[sceneObject.layerId];
        if (layer?.locked) return;
    } else {
        // Fallback for ids that predate the ledger (kernel-internal pickers
        // resolving raw element ids in the BIM viewport).
        const entity = useAppStore.getState().entities[entityId];
        if (entity && (!entity.visible || entity.locked)) return;
    }
    mainRouter.current?.(entityId, event);
    listeners.forEach((listener) => listener(entityId, event));
}

/** React hook: subscribe a listener for the handler's lifetime. */
export function useEntityClickListener(handler: EntityClickHandler, deps: unknown[]): void {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => addEntityClickListener(handler), deps);
}

/**
 * Live registry of entity root objects (id -> Object3D).
 * Written by EntityLayer ref callbacks; kept for downstream consumers that
 * resolve entities from the scene graph via userData.entityId.
 */
export const entityMeshRegistry = new Map<string, THREE.Object3D>();

/**
 * Marks a canvas as a navigation-preview viewport (quad-view mini cells).
 * Preview canvases render the shared document read-only: entity meshes mount
 * in a SECOND WebGL context, so they must never claim registry slots — the
 * interactive viewport's objects own those ids. Click routing stays live so
 * picking works in every viewport (Rhino semantics).
 */
export const PreviewViewportContext = createContext(false);

/** True when the calling component renders inside a preview viewport. */
export function useIsPreviewViewport(): boolean {
    return useContext(PreviewViewportContext);
}

/**
 * Stable ref callback that tags an entity's root object and claims its
 * registry slot — unless the component lives in a preview viewport, where
 * registration is suppressed (see PreviewViewportContext). Stable identity
 * also stops React from re-invoking the ref (delete+set churn) every commit.
 */
export function useEntityRegistryRef(entityId: string, layer?: string) {
    const isPreview = useIsPreviewViewport();
    return useCallback(
        (obj: THREE.Object3D | null) => {
            if (isPreview) {
                // Preview canvases never claim slots; also never delete on
                // detach — the slot belongs to the interactive viewport's
                // object, not this preview clone.
                if (obj) {
                    obj.userData.entityId = entityId;
                    if (layer !== undefined) obj.userData.layer = layer;
                }
                return;
            }
            if (obj) {
                obj.userData.entityId = entityId;
                if (layer !== undefined) obj.userData.layer = layer;
                entityMeshRegistry.set(entityId, obj);
            } else {
                entityMeshRegistry.delete(entityId);
            }
        },
        [entityId, layer, isPreview]
    );
}

